<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  updateGsplatBackendControlState,
  type GsplatBackendControlState,
} from '../utils/gsplatBackendControl'

const props = defineProps<{
  state: GsplatBackendControlState
}>()

const effectiveSorting = computed(() =>
  props.state.backend === 'webgpu' ? props.state.sorting : 'cpu'
)

const runtimeStatus = ref<{
  backend: string | null
  requestedSorting: string
  resolvedSorting: string
}>({
  backend: null,
  requestedSorting: props.state.sorting,
  resolvedSorting: '等待引擎',
})
let runtimeProbeTimer: number | null = null

function probeRuntimeStatus(): void {
  type RuntimeEngine = {
    getGraphicsInfo?: () => { kind?: string }
    plugins?: {
      get?: (name: string) => {
        getConfig?: () => { sortingMode?: string }
        resolvedSortingMode?: string
      } | undefined
    }
  }
  const engine = Reflect.get(window, 'engine') as RuntimeEngine | undefined
  const plugin = engine?.plugins?.get?.('gaussian-splat')
  const config = plugin?.getConfig?.()
  runtimeStatus.value = {
    backend: engine?.getGraphicsInfo?.().kind ?? null,
    requestedSorting: config?.sortingMode ?? props.state.sorting,
    resolvedSorting: plugin?.resolvedSortingMode ?? '等待引擎',
  }
  if (!engine || !plugin) {
    runtimeProbeTimer = window.setTimeout(probeRuntimeStatus, 250)
  }
}

function update(key: 'sorting' | 'radix' | 'pickIds', value: string): void {
  const next = {
    ...props.state,
    [key]: value,
  } as {
    backend: GsplatBackendControlState['backend']
    sorting: GsplatBackendControlState['sorting']
    radix: GsplatBackendControlState['radix']
    pickIds: GsplatBackendControlState['pickIds']
  }
  if (key === 'sorting' && value === 'cpu') {
    next.radix = 'auto'
  }
  updateGsplatBackendControlState(next)
}

onMounted(probeRuntimeStatus)
onBeforeUnmount(() => {
  if (runtimeProbeTimer !== null) {
    window.clearTimeout(runtimeProbeTimer)
    runtimeProbeTimer = null
  }
})
</script>

<template>
  <section class="gsplat-backend-card">
    <header class="gsplat-backend-card-header">
      <div>
        <p class="gsplat-backend-card-kicker">3DGS Runtime</p>
        <h3>渲染后端与捕捉</h3>
      </div>
      <span class="gsplat-backend-card-badge">{{ props.state.backend }}</span>
    </header>

    <label class="gsplat-backend-field">
      <span>排序</span>
      <select
        :value="effectiveSorting"
        :disabled="props.state.backend !== 'webgpu'"
        @change="update('sorting', ($event.target as HTMLSelectElement).value)"
      >
        <option value="auto">引擎默认（WebGPU: GPU / Hybrid）</option>
        <option value="cpu">CPU / Quad</option>
        <option value="gpu">GPU / Hybrid</option>
      </select>
    </label>

    <label class="gsplat-backend-field">
      <span>GPU radix</span>
      <select
        :value="props.state.radix"
        :disabled="props.state.backend !== 'webgpu' || props.state.sorting === 'cpu'"
        @change="update('radix', ($event.target as HTMLSelectElement).value)"
      >
        <option value="auto">引擎默认（NVIDIA OneSweep / 其他 Portable）</option>
        <option value="portable">Portable Multipass</option>
        <option value="onesweep">OneSweep</option>
      </select>
    </label>

    <label class="gsplat-backend-field">
      <span>GPU pick ID</span>
      <select
        :value="props.state.pickIds"
        :disabled="props.state.backend !== 'webgpu'"
        @change="update('pickIds', ($event.target as HTMLSelectElement).value)"
      >
        <option value="direct">direct cache ID</option>
        <option value="buffer">buffer pickIds</option>
      </select>
    </label>

    <p class="gsplat-backend-card-note">
      <template v-if="props.state.backend !== 'webgpu'">
        WebGL2 固定使用 CPU 排序；GPU 排序、radix 与 pick ID 仅在 WebGPU 下生效。
      </template>
      <template v-else>
        切换后会按公开配置重建当前示例；auto 由引擎按后端和设备能力解析，WebGL 固定使用
        CPU 排序。
      </template>
    </p>

    <div class="gsplat-backend-status" aria-live="polite">
      <span>运行时后端</span>
      <strong>{{ runtimeStatus.backend ?? '等待引擎' }}</strong>
      <span>排序 requested / resolved</span>
      <strong>{{ runtimeStatus.requestedSorting }} / {{ runtimeStatus.resolvedSorting }}</strong>
      <span>捕捉</span>
      <strong>{{ props.state.backend === 'webgpu' ? '异步 GPU（支持 gpuOnly）' : '同步 CPU' }}</strong>
    </div>
  </section>
</template>

<style scoped>
.gsplat-backend-card {
  display: grid;
  gap: 10px;
  padding: 14px;
  border: 1px solid rgba(148, 163, 184, 0.24);
  border-radius: 14px;
  background: rgba(15, 23, 42, 0.72);
  color: #e2e8f0;
}

.gsplat-backend-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.gsplat-backend-card-kicker {
  margin: 0 0 4px;
  color: #94a3b8;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.gsplat-backend-card h3 {
  margin: 0;
  font-size: 15px;
}

.gsplat-backend-card-badge {
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.16);
  color: #7dd3fc;
  font-size: 11px;
  text-transform: uppercase;
}

.gsplat-backend-field {
  display: grid;
  grid-template-columns: minmax(88px, 1fr) minmax(0, 1.5fr);
  align-items: center;
  gap: 8px;
  color: #cbd5e1;
  font-size: 12px;
}

.gsplat-backend-field select {
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid rgba(148, 163, 184, 0.3);
  border-radius: 8px;
  background: rgba(15, 23, 42, 0.95);
  color: #f8fafc;
  font: inherit;
}

.gsplat-backend-field select:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.gsplat-backend-card-note {
  margin: 0;
  color: #94a3b8;
  font-size: 11px;
  line-height: 1.45;
}

.gsplat-backend-status {
  display: grid;
  grid-template-columns: minmax(92px, 1fr) minmax(0, 1.4fr);
  gap: 4px 8px;
  padding-top: 4px;
  border-top: 1px solid rgba(148, 163, 184, 0.16);
  color: #94a3b8;
  font-size: 11px;
}

.gsplat-backend-status strong {
  color: #cbd5e1;
  font-weight: 600;
}
</style>
